import { useCallback } from 'react';
import { useBlog } from '../../hooks/useBlog';
import { PostsListView } from './blog/PostsListView';
import { PostEditorView } from './blog/PostEditorView';
import { AnalyticsView } from './blog/AnalyticsView';
import { SettingsView } from './blog/SettingsView';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { FileText, BarChart3, Settings } from 'lucide-react';
import type { BlogTab } from '../../types';

interface BlogPageProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
}

export function BlogPage({ accessToken, refreshToken, attemptRefresh }: BlogPageProps) {
  const blog = useBlog({
    accessToken: accessToken ?? undefined,
    refreshToken: refreshToken ?? undefined,
    attemptRefresh,
  });

  const handleCreatePost = useCallback(async () => {
    const result = await blog.handleCreatePost({ title: 'Novo Post' });
    if (result.success && result.post?.id) {
      await blog.loadPost(result.post.id);
      blog.setEditingPostId(result.post.id);
    }
  }, [blog]);

  const handleEditPost = useCallback(
    (id: string) => {
      blog.loadPost(id);
      blog.setEditingPostId(id);
    },
    [blog]
  );

  const handleBackFromEditor = useCallback(() => {
    blog.setEditingPostId(null);
    blog.loadPosts();
  }, [blog]);

  const handleTabChange = useCallback(
    (value: string) => {
      blog.setBlogTab(value as BlogTab);
    },
    [blog]
  );

  // If editing a post, show the editor full-screen
  // Wait for post data to load before rendering the editor so Craft.js
  // can deserialize the saved content. While loading, show a spinner.
  if (blog.editingPostId) {
    if (!blog.currentPost || blog.currentPost.id !== blog.editingPostId) {
      return (
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <p className="text-muted-foreground text-sm">Carregando post...</p>
        </div>
      );
    }
    return (
      <PostEditorView
        key={blog.currentPost.id}
        post={blog.currentPost}
        categories={blog.categories}
        tags={blog.tags}
        loading={blog.loading}
        accessToken={accessToken}
        onSave={async (data) => {
          await blog.handleUpdatePost(blog.editingPostId!, data);
        }}
        onAutoSave={async (content) => {
          await blog.handleAutoSave(blog.editingPostId!, content);
        }}
        onPublish={async () => {
          await blog.handlePublishPost(blog.editingPostId!);
          handleBackFromEditor();
        }}
        onBack={handleBackFromEditor}
        onCreateCategory={blog.handleCreateCategory}
        onCreateTag={blog.handleCreateTag}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Blog</h1>
      </div>

      <Tabs value={blog.blogTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList>
          <TabsTrigger value="posts" className="gap-2">
            <FileText className="h-4 w-4" />
            Posts
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            Configurações
          </TabsTrigger>
        </TabsList>

        <TabsContent value="posts">
          <PostsListView
            posts={blog.posts}
            categories={blog.categories}
            totalPosts={blog.totalPosts}
            page={blog.postsPage}
            loading={blog.loading}
            onCreatePost={handleCreatePost}
            onEditPost={handleEditPost}
            onDeletePost={(id) => { void blog.handleDeletePost(id); }}
            onPublishPost={(id) => { void blog.handlePublishPost(id); }}
            onFilterChange={(status, search, page) =>
              void blog.loadPosts(status, search, page)
            }
          />
        </TabsContent>

        <TabsContent value="analytics">
          <AnalyticsView
            analytics={blog.analytics}
            loading={blog.loading}
            onPeriodChange={(from, to) => void blog.loadAnalytics(from, to)}
          />
        </TabsContent>

        <TabsContent value="settings">
          <SettingsView
            categories={blog.categories}
            tags={blog.tags}
            fonts={blog.fonts}
            loading={blog.loading}
            onCreateCategory={async (data) => {
              await blog.handleCreateCategory(data);
            }}
            onUpdateCategory={async (id, data) => {
              await blog.handleUpdateCategory(id, data);
            }}
            onDeleteCategory={async (id) => {
              await blog.handleDeleteCategory(id);
            }}
            onCreateTag={async (name) => {
              await blog.handleCreateTag(name);
            }}
            onDeleteTag={async (id) => {
              await blog.handleDeleteTag(id);
            }}
            onUploadFont={async (file, family) => {
              await blog.handleUploadFont(file, family);
            }}
            onDeleteFont={async (id) => {
              await blog.handleDeleteFont(id);
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
